import { gql, useQuery } from '@apollo/client';

import Entity from '../../components/entity';

import type {
  GetAllFinancialAccountsQuery,
  GetAllFinancialAccountsQueryVariables,
} from './__generated__/useFinancialAccounts.gql';

export const FINANCIAL_ACCOUNT_FRAGMENT = gql`
  fragment FinancialAccountFields on FinancialAccount {
    id
    name
    type
    subtype
    mask
    lastSyncedAt
    ...EntityAvatar
    bankConnection {
      id
      lastSyncedAt
      historicalSyncComplete
      hasPotentialSyncIssue
      needsReconnection
      canAddInvestments
    }
    bank {
      id
      name
    }
    currentBalance {
      id
      balance
      creditLimit
      timestamp
    }
  }
  ${Entity.Avatar.Fragment}
`;

export const GET_ALL_FINANCIAL_ACCOUNTS = gql`
  query GetAllFinancialAccounts {
    getFinancialAccountsForUser {
      ...FinancialAccountFields
    }
  }
  ${FINANCIAL_ACCOUNT_FRAGMENT}
`;

export type FinancialAccount = GetAllFinancialAccountsQuery['getFinancialAccountsForUser'][number];

/**
 * Every financial account the viewer's family holds, with the query's loading
 * and error state.
 *
 * `refetch` is exposed because a Link session's accounts arrive on a webhook
 * rather than in the mutation response, so a caller waiting on one has no cache
 * write to react to and must ask again.
 */
export const useFinancialAccounts = () => {
  const { data, loading, error, refetch } = useQuery<
    GetAllFinancialAccountsQuery,
    GetAllFinancialAccountsQueryVariables
  >(GET_ALL_FINANCIAL_ACCOUNTS);

  return { financialAccounts: data?.getFinancialAccountsForUser ?? [], loading, error, refetch };
};
